import { useEffect } from 'react';
import { Box, VStack, Heading, Text, Button, HStack, Wrap, WrapItem } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Check, Sparkles, TrendingUp } from 'lucide-react';
import RatingEmoji from '@/components/feedback/RatingEmoji';
import { RATING_EMOJIS } from '@/utils/restaurantUtils';
import { useCelebration } from '@/hooks/useCelebration';
import { haptics } from '@/utils/haptics';
import type { RestaurantRatings } from '@/types/ratings';
import type { RatingMenuItem } from '../hooks/useRestaurantRating';

const MotionBox = motion.create(Box);

interface CompletionStepProps {
  /** Restaurant ratings summary */
  ratings: RestaurantRatings;
  /** Menu items rated */
  menuItems: RatingMenuItem[];
  /** Callback to close the modal */
  onDone: () => void;
}

// Get emoji from rating value
const getEmojiFromValue = (value: number) => {
  return RATING_EMOJIS.find((e) => e.value === value) ?? RATING_EMOJIS[0];
};

// Category display names
const CATEGORY_LABELS: Record<keyof RestaurantRatings, string> = {
  atmosphere: 'Atmosphere',
  service: 'Service',
  value: 'Value',
  presentation: 'Presentation',
  food: 'Food',
  overall: 'Overall',
};

/**
 * Completion step with rating summary and confetti celebration
 */
export function CompletionStep({
  ratings,
  menuItems,
  onDone,
}: CompletionStepProps) {
  const { fire: fireConfetti } = useCelebration();

  // Fire confetti on mount
  useEffect(() => {
    // Small delay to ensure modal is visible
    const timer = setTimeout(() => {
      fireConfetti({ type: 'confetti', withSideBursts: true, zIndex: 10001 });
      haptics.celebrate();
    }, 300);

    return () => clearTimeout(timer);
  }, [fireConfetti]);

  // Count rated categories
  const ratedCategories = Object.values(ratings).filter((v) => v > 0).length;
  const ratedItems = menuItems.filter((item) => item.rating > 0).length;

  return (
    <VStack spacing={6} align="center" w="100%" px={2} data-testid="rating-completion">
      {/* Success icon */}
      <MotionBox
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
      >
        <Box
          bg="green.500"
          borderRadius="full"
          p={4}
          boxShadow="0 0 30px var(--chakra-colors-green-500)"
        >
          <Check size={40} color="white" strokeWidth={3} />
        </Box>
      </MotionBox>

      {/* Thank you message */}
      <VStack spacing={2} textAlign="center">
        <Heading size="lg" color="white" fontWeight="bold">
          Thanks for rating!
        </Heading>
        <Text color="gray.400" fontSize="md" maxW="280px">
          Your ratings help improve your TasteMatch™ and help others discover great food.
        </Text>
      </VStack>

      {/* PersonalTaste message */}
      <Box
        bg="purple.900"
        borderRadius="var(--r-card-compact)"
        p={4}
        w="100%"
        maxW="320px"
      >
        <HStack spacing={3}>
          <Box color="purple.300">
            <Sparkles size={20} />
          </Box>
          <VStack align="start" spacing={0}>
            <Text color="purple.200" fontWeight="semibold" fontSize="sm">
              PersonalTaste Updated
            </Text>
            <Text color="gray.400" fontSize="xs">
              Your profile is now more accurate
            </Text>
          </VStack>
          <Box color="green.400" ml="auto">
            <TrendingUp size={20} />
          </Box>
        </HStack>
      </Box>

      {/* Rating summary */}
      {ratedCategories > 0 && (
        <Box
          bg="whiteAlpha.100"
          borderRadius="var(--r-card-compact)"
          p={4}
          w="100%"
          maxW="320px"
        >
          <Text fontSize="sm" color="gray.500" fontWeight="medium" mb={3}>
            Your ratings
          </Text>
          {/* Wrap instead of SimpleGrid: items flow naturally when fewer than
              5 categories are rated or null entries are filtered out, avoiding
              the empty-cell gaps that a fixed columns={5} grid produces. */}
          <Wrap spacing={2} justify="center">
            {(Object.entries(ratings) as [keyof RestaurantRatings, number][]).map(
              ([category, value]) => {
                if (value === 0 || category === 'overall') return null;
                const emoji = getEmojiFromValue(value);
                return (
                  <WrapItem key={category}>
                    <VStack spacing={1}>
                      <RatingEmoji emoji={emoji} size="lg" showBackground />
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        {CATEGORY_LABELS[category].slice(0, 4)}
                      </Text>
                    </VStack>
                  </WrapItem>
                );
              }
            )}
          </Wrap>
        </Box>
      )}

      {/* Menu items summary */}
      {ratedItems > 0 && (
        <Text fontSize="sm" color="gray.500">
          + {ratedItems} dish{ratedItems > 1 ? 'es' : ''} rated
        </Text>
      )}

      {/* Done button */}
      <Button
        colorScheme="purple"
        size="lg"
        w="100%"
        maxW="320px"
        onClick={onDone}
      >
        Done
      </Button>
    </VStack>
  );
}

export default CompletionStep;
